import { useState } from "react";
import { Sheet, IconButton, Select } from "./ds";
import { st } from "../../styles.js";
import { WEEKDAYS_SK, ymd, todayKey } from "../../lib/format";
import { buildMonthCells } from "../../lib/calendar";

// v26.3 §1 — dátumový picker s mesačnou mriežkou (nahrádza natívny <input type="date">),
// aby bol pri výbere jasný deň v týždni. value/onChange/min pracujú s čistým dátumom "YYYY-MM-DD".

function fmtDate(key) {
  const d = new Date(key + "T00:00:00");
  if (isNaN(d.getTime())) return key;
  const dow = WEEKDAYS_SK[(d.getDay() + 6) % 7].toLowerCase();
  return `${dow} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

// datetime "YYYY-MM-DDTHH:MM" ⇄ {date, hh, mm}; prázdny dátum = prázdna hodnota.
// Staré dáta s časom mimo 5-minútového rastra sa pri editácii zaokrúhlia NADOL.
export function splitDT(v) {
  if (!v) return { date: "", hh: "08", mm: "00" };
  const [date, time] = String(v).split("T");
  const [rawH = "08", rawM = "00"] = (time || "").split(":");
  const mm = String(Math.floor((parseInt(rawM, 10) || 0) / 5) * 5).padStart(2, "0");
  return { date: date || "", hh: (rawH || "08").padStart(2, "0"), mm };
}
export function joinDT(date, hh, mm) {
  if (!date) return "";
  return `${date}T${(hh || "08").padStart(2, "0")}:${(mm || "00").padStart(2, "0")}`;
}

// v26.3 §1 — dátum (kalendár) + čas (HH/MM po 5 min) namiesto natívneho datetime-local.
// value/onChange pracujú s pôvodným formátom "YYYY-MM-DDTHH:MM"; prázdny dátum = prázdna hodnota.
// timeOptional — čas nie je predvyplnený; kým ho používateľ nevyberie, ukáže sa sivý
// placeholder „––" (nie mätúcich „08:00"). Hodnota je vtedy len dátum „YYYY-MM-DD".
export function DateTimeField({ value, onChange, min, label, allowClear, timeOptional }) {
  const raw = String(value || "");
  const datePart = raw.split("T")[0] || "";
  const timeStr = raw.includes("T") ? raw.split("T")[1] : "";
  const hasTime = /^\d{2}:\d{2}/.test(timeStr);
  const { hh, mm } = splitDT(hasTime ? raw : (datePart ? datePart + "T08:00" : ""));
  const showPh = !!timeOptional && !hasTime;  // sivý placeholder namiesto konkrétneho času
  const phStyle = showPh ? { color: "var(--neutral-text-subtle)" } : {};
  const mins = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
  if (hasTime && mm && !mins.includes(mm)) mins.push(mm);
  const minDate = min ? splitDT(min).date : undefined;
  const setDate = (d) => onChange(!d ? "" : (showPh ? d : joinDT(d, hh, mm)));  // v timeOptional nechaj len dátum
  return (
    <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", flex: 1, minWidth: 0 }}>
      <DateField value={datePart} onChange={setDate} min={minDate} label={label} allowClear={allowClear} />
      <Select value={showPh ? "" : hh} style={{ width: 78, fontSize: 16, ...phStyle }} aria-label="Hodina" disabled={!datePart}
        onChange={(e) => onChange(joinDT(datePart, e.target.value, hasTime ? mm : "00"))}>
        {showPh && <option value="" disabled hidden>––</option>}
        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => <option key={h} value={h}>{h}</option>)}
      </Select>
      <span style={{ fontWeight: 700, color: "var(--muted)" }}>:</span>
      <Select value={showPh ? "" : mm} style={{ width: 78, fontSize: 16, ...phStyle }} aria-label="Minúta" disabled={!datePart}
        onChange={(e) => onChange(joinDT(datePart, hasTime ? hh : "08", e.target.value))}>
        {showPh && <option value="" disabled hidden>––</option>}
        {mins.sort().map((m) => <option key={m} value={m}>{m}</option>)}
      </Select>
    </span>
  );
}

export function DateField({ value, onChange, min, allowClear, label }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const b = value ? new Date(value + "T00:00:00") : new Date();
    return { y: b.getFullYear(), m: b.getMonth() };
  });
  const cells = buildMonthCells(view.y, view.m);
  const tKey = todayKey();
  const monthName = new Date(view.y, view.m, 1).toLocaleDateString("sk-SK", { month: "long", year: "numeric" });
  function shift(delta) {
    setView((p) => { const d = new Date(p.y, p.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  }
  function openSheet() {
    const b = value ? new Date(value + "T00:00:00") : new Date();
    setView({ y: b.getFullYear(), m: b.getMonth() });
    setOpen(true);
  }
  return (
    <>
      <span style={{ display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 0 }}>
        <button type="button" onClick={openSheet} className="bz-input"
          style={{ textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}
          aria-label={label || "Vyber dátum"}>
          {value ? fmtDate(value) : <span style={{ color: "var(--neutral-text-subtle)" }}>Vyber dátum</span>}
        </button>
        {allowClear && value && (
          <button type="button" onClick={() => onChange("")} style={st.iconBtn} title="Zmazať dátum">✕</button>
        )}
      </span>
      {open && (
        <Sheet center title="Vyber dátum" icon="calendar" onClose={() => setOpen(false)}>
          <div style={st.calHead}>
            <IconButton variant="surface" size="sm" icon="chevron" aria-label="Predchádzajúci mesiac"
              onClick={() => shift(-1)} style={{ transform: "rotate(180deg)" }} />
            <span style={st.calTitle}>{monthName}</span>
            <IconButton variant="surface" size="sm" icon="chevron" aria-label="Nasledujúci mesiac" onClick={() => shift(1)} />
          </div>
          <div style={st.calGrid}>
            {WEEKDAYS_SK.map((w) => <div key={w} style={st.calDow}>{w}</div>)}
            {cells.map((d, i) => {
              if (d === null) return <div key={"e" + i} style={st.calEmpty} />;
              const key = ymd(view.y, view.m, d);
              const isToday = key === tKey;
              const isSel = key === value;
              const disabled = min && key < min;
              return (
                <button key={key} type="button" disabled={disabled}
                  onClick={() => { onChange(key); setOpen(false); }}
                  style={{ ...st.calCell, ...(isToday ? st.calCellToday : {}),
                    ...(isSel ? { background: "var(--brand-primary)", borderColor: "var(--brand-primary)" } : {}),
                    ...(disabled ? { opacity: 0.3, cursor: "not-allowed" } : {}) }}>
                  <span style={{ ...st.calDayNum, ...(isToday ? st.calDayNumToday : {}),
                    ...(isSel ? { color: "var(--brand-on-primary)" } : {}) }}>{d}</span>
                </button>
              );
            })}
          </div>
        </Sheet>
      )}
    </>
  );
}
